import React, { useEffect, useState } from "react";
import { Container, Card, Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ToastContainer } from "react-toastify";

import store from "./store";
import { setTemplate } from "./store/templateReducer";
import { setZipList, setZipListLoading } from "./store/helpersReducer";

import DownloadJsonDialog from "./components/DownloadJson";
import DownloadTgzDialog from "./components/DownloadTgz";
import DownloadPdfDialog from "./components/DownloadPdf";
import { listenToDragDrop } from "./utils/functions";
import {
  generateTemplatesFromJSON,
  generateTemplatesToJSON,
} from "./utils/templateUtils";
import Remote from "./components/Remote";
import Navigation from "./components/Navigation";
import TemplatePopup from "./components/Template/TemplatePopup";
import TemplateHistoryPopup from "./components/TemplateHistory/TemplateHistoryPopup";
import {
  makeGetFeatureLisBlob,
  makeGetFeatureListDownload,
  makeUpdateFeatureList,
} from "./backend/featureList";
import { makePostArchiveTarball } from "./backend/zip";
import { makeGetZipList } from "./backend/zipList";
import BoardLogPopup from "./components/BoardLog/BoardLogPopup";

listenToDragDrop();

const useStyles = makeStyles(() => ({
  container: {
    marginTop: 75,
  },
  btnWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 10,
  },
  rightShift: {
    marginRight: 10,
  },
  card: {
    padding: "2rem",
  },
}));

export default () => {
  const classes = useStyles();
  const [isDownloadJsonOpen, setIsDownloadJsonOpen] = useState(false);
  const [isDownloadTgzOpen, setIsDownloadTgzOpen] = useState(false);
  const [isDownloadPdfOpen, setIsDownloadPdfOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [openSuccessImport, setOpenSuccessImport] = useState(false);
  const [openErrorImport, setOpenErrorImport] = useState(false);
  const [templateHistoryIsOpen, setTemplateHistoryIsOpen] = useState(false);
  const [boardLogOpen, setBoardLogOpen] = useState(false);
  const [popupLoading, setPopupLoading]=useState(false)

  useEffect(() => {
    getData();
    loadConfig();
  }, []);

  const savePopup = async () => {
    try {
      setPopupLoading(true)
      if (templates.length) {
        const result = generateTemplatesToJSON(templates);
        await makePostArchiveTarball(result);
        await makeUpdateFeatureList(result);
        store.dispatch(setTemplate(result));
      }
    } catch (e) {
      console.error(e);
    } finally {
      closePopup();
      setPopupLoading(false)
    }
  };

  const closePopup = () => {
    setTemplates([]);
    setOpen(false);
  };

  const loadConfig = async () => {
    store.dispatch(setZipListLoading(true));
    const { data: zipList } = await makeGetZipList();
    store.dispatch(setZipList(zipList));
    store.dispatch(setZipListLoading(false));
  };

  const getData = async () => {
    setLoader(true);
    try {
      const { data = [] } = await makeGetFeatureListDownload();
      store.dispatch(setTemplate(data));
      setTemplates(generateTemplatesFromJSON(data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (open) {
      getData();
    }
  }, [open]);

  const exportTemplates = async () => {
    try {
      const { data } = await makeGetFeatureLisBlob();
      const newBlob = new Blob([data], { type: "application/json" });
      const blobUrl = window.URL.createObjectURL(newBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `templates.json`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      // clean up Url
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error(e);
    }
  };

  const importTemplates = ({ target: { files } }) => {
    const reader = new FileReader();
    reader.onload = async ({ target: { result } }) => {
      try {
        await makeUpdateFeatureList(JSON.parse(result));
        setOpenSuccessImport(true);
      } catch (e) {
        console.error(e);
        setOpenErrorImport(true);
      }
    };
    // Read as json file
    reader.readAsText(files[0]);
  };

  return (
    <div className="App">
      <Navigation setTemplateHistoryIsOpen={setTemplateHistoryIsOpen} setBoardLogOpen={setBoardLogOpen} />
      <ToastContainer />
      <Container className={classes.container}>
        <div className={classes.btnWrapper}>
          <Button
            className={classes.rightShift}
            disabled={false}
            variant="contained"
            color="primary"
            onClick={() => setIsDownloadJsonOpen(true)}
          >
            Download JSON
          </Button>
          <Button
            className={classes.rightShift}
            disabled={false}
            variant="contained"
            color="primary"
            onClick={() => setIsDownloadTgzOpen(true)}
          >
            Download TGZ
          </Button>
          <Button
            className={classes.rightShift}
            disabled={false}
            variant="contained"
            color="primary"
            onClick={() => setIsDownloadPdfOpen(true)}
          >
            Download PDF
          </Button>
          <Button
            className={classes.rightShift}
            disabled={false}
            variant="contained"
            color="primary"
            component="label"
          >
            Import templates
            <input
              type="file"
              accept=".json"
              hidden
              onChange={importTemplates}
            />
          </Button>
          <Button
            className={classes.rightShift}
            disabled={!setTemplates.length}
            variant="contained"
            color="primary"
            onClick={() => exportTemplates()}
          >
            Export templates
          </Button>
          <Button
            disabled={false}
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
          >
            Templates
          </Button>
        </div>
        <DownloadJsonDialog
          isDownloadJsonOpen={isDownloadJsonOpen}
          close={() => setIsDownloadJsonOpen(false)}
        />
        <DownloadTgzDialog
          isDownloadTgzOpen={isDownloadTgzOpen}
          close={() => setIsDownloadTgzOpen(false)}
        />
        <DownloadPdfDialog
          isDownloadPdfOpen={isDownloadPdfOpen}
          close={() => setIsDownloadPdfOpen(false)}
        />
        <Card className={classes.card}>
          <Remote />
        </Card>
        <TemplatePopup
          open={open}
          popupLoading={popupLoading}
          onSave={savePopup}
          onClose={closePopup}
          templates={templates}
          setTemplates={setTemplates}
          loader={loader}
          setOpen={setOpen}
        />
        <TemplateHistoryPopup
          templateHistoryIsOpen={templateHistoryIsOpen}
          setTemplateHistoryIsOpen={setTemplateHistoryIsOpen}
        />
        <BoardLogPopup
          boardLogOpen={boardLogOpen}
          setBoardLogOpen={setBoardLogOpen}
        />
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={openSuccessImport}
          autoHideDuration={6000}
          onClose={() => setOpenSuccessImport(false)}
        >
          <Alert onClose={() => setOpenSuccessImport(false)} severity="success">
            The file has successfully uploaded!
          </Alert>
        </Snackbar>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={openErrorImport}
          autoHideDuration={6000}
          onClose={() => setOpenErrorImport(false)}
        >
          <Alert onClose={() => setOpenErrorImport(false)} severity="error">
            Oops! Something wrong...Try to upload the file again
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};
